import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, MapPin, Building, RefreshCw, CheckCircle, X } from 'lucide-react';
import api from '@/utils/api';

const CityManagement = () => {
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [isSeeded, setIsSeeded] = useState(false);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  
  // Form data for Add dialog - support multiple cities
  const [newCity, setNewCity] = useState({ state: '', district: '', city: '' });
  const [multipleCities, setMultipleCities] = useState([]); // Array of city names to add
  const [cityInput, setCityInput] = useState(''); // Current input for city name
  const [addDialogDistricts, setAddDialogDistricts] = useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');
  const [existingCitiesInDistrict, setExistingCitiesInDistrict] = useState([]);
  
  // Form data for Edit dialog
  const [editForm, setEditForm] = useState({ id: '', state: '', district: '', city: '' });
  const [editDialogDistricts, setEditDialogDistricts] = useState([]);

  useEffect(() => {
    fetchStates();
    fetchCities();
  }, []);

  useEffect(() => {
    if (selectedState) {
      fetchDistrictsByState(selectedState);
    } else {
      setDistricts([]);
      setSelectedDistrict('');
    }
  }, [selectedState]);

  useEffect(() => {
    fetchCities();
  }, [selectedState, selectedDistrict]);

  // Fetch districts for Add dialog when state changes
  useEffect(() => {
    if (newCity.state) {
      fetchDistrictsForAddDialog(newCity.state);
    } else {
      setAddDialogDistricts([]);
    }
  }, [newCity.state]);

  // Fetch districts for Edit dialog when state changes
  useEffect(() => {
    if (editForm.state) {
      fetchDistrictsForEditDialog(editForm.state);
    } else {
      setEditDialogDistricts([]);
    }
  }, [editForm.state]);

  // Fetch existing cities when state and district are selected (for duplicate checking)
  useEffect(() => {
    const fetchExistingCities = async () => {
      if (newCity.state && newCity.district) {
        try {
          const response = await api.get(`/location-master/cities?state=${encodeURIComponent(newCity.state)}&district=${encodeURIComponent(newCity.district)}`);
          setExistingCitiesInDistrict(response.data.map(c => c.city.toLowerCase()));
        } catch (error) {
          setExistingCitiesInDistrict([]);
        }
      } else {
        setExistingCitiesInDistrict([]);
      }
    };
    fetchExistingCities();
  }, [newCity.state, newCity.district]);

  // Check for duplicate city when city input changes
  useEffect(() => {
    if (cityInput && cityInput.length >= 2 && existingCitiesInDistrict.length > 0) {
      const exists = existingCitiesInDistrict.includes(cityInput.toLowerCase());
      const alreadyAdded = multipleCities.some(c => c.toLowerCase() === cityInput.toLowerCase());
      if (exists) {
        setDuplicateError(`"${cityInput}" already exists in this district`);
      } else if (alreadyAdded) {
        setDuplicateError(`"${cityInput}" is already in your list`);
      } else {
        setDuplicateError('');
      }
    } else {
      setDuplicateError('');
    }
  }, [cityInput, existingCitiesInDistrict, multipleCities]);

  const fetchStates = async () => {
    try {
      const response = await api.get('/location-master/states');
      setStates(response.data);
    } catch (error) {
      console.error('Failed to fetch states');
    }
  };

  const fetchDistrictsByState = async (state) => {
    try {
      const response = await api.get(`/location-master/districts?state=${encodeURIComponent(state)}`);
      setDistricts(response.data);
    } catch (error) {
      console.error('Failed to fetch districts');
    }
  };

  const fetchDistrictsForAddDialog = async (state) => {
    try {
      const response = await api.get(`/location-master/districts?state=${encodeURIComponent(state)}`);
      setAddDialogDistricts(response.data);
    } catch (error) {
      console.error('Failed to fetch districts for add dialog');
    }
  };

  const fetchDistrictsForEditDialog = async (state) => {
    try {
      const response = await api.get(`/location-master/districts?state=${encodeURIComponent(state)}`);
      setEditDialogDistricts(response.data);
    } catch (error) {
      console.error('Failed to fetch districts for edit dialog');
    }
  };

  const fetchCities = async () => {
    try {
      setLoading(true);
      let url = '/location-master/cities';
      const params = [];
      if (selectedState) params.push(`state=${encodeURIComponent(selectedState)}`);
      if (selectedDistrict) params.push(`district=${encodeURIComponent(selectedDistrict)}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await api.get(url);
      setCities(response.data);
      // Check if seeding has been done (has more than 50 cities)
      if (response.data.length >= 50) {
        setIsSeeded(true);
      }
    } catch (error) {
      toast.error('Failed to fetch cities');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) {
      toast.error('Enter at least 2 characters to search');
      return;
    }
    
    try {
      const response = await api.get(`/location-master/cities/search?q=${encodeURIComponent(searchQuery)}&limit=100`);
      setCities(response.data);
      setSelectedState('');
      setSelectedDistrict('');
    } catch (error) {
      toast.error('Search failed');
    }
  };

  // Add city to the list
  const handleAddCityToList = () => {
    if (!cityInput.trim()) return;
    if (duplicateError) {
      toast.error(duplicateError);
      return;
    }
    
    setMultipleCities([...multipleCities, cityInput.trim()]);
    setCityInput('');
    setDuplicateError('');
  };

  // Remove city from the list
  const handleRemoveCityFromList = (index) => {
    setMultipleCities(multipleCities.filter((_, i) => i !== index));
  };

  // Save all cities at once
  const handleSaveAllCities = async () => {
    if (!newCity.state || !newCity.district) {
      toast.error('State and district are required');
      return;
    }
    
    if (multipleCities.length === 0) {
      toast.error('Add at least one city to the list');
      return;
    }
    
    try {
      // Add all cities via bulk endpoint
      const response = await api.post('/location-master/cities/bulk', {
        state: newCity.state,
        district: newCity.district,
        cities: multipleCities
      });
      
      const added = response.data.added || multipleCities.length;
      const skipped = response.data.skipped || 0;
      
      if (skipped > 0) {
        toast.success(`Added ${added} cities. ${skipped} duplicates skipped.`);
      } else {
        toast.success(`Successfully added ${added} cities!`);
      }
      
      setShowAddDialog(false);
      setNewCity({ state: '', district: '', city: '' });
      setMultipleCities([]);
      setCityInput('');
      setAddDialogDistricts([]);
      setDuplicateError('');
      setExistingCitiesInDistrict([]);
      fetchCities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add cities');
    }
  };

  // Legacy single city add (keeping for backward compatibility)
  const handleAddCity = async () => {
    if (!newCity.state || !newCity.district || !newCity.city) {
      toast.error('State, district and city name are required');
      return;
    }
    
    if (duplicateError) {
      toast.error('This city already exists');
      return;
    }
    
    try {
      await api.post('/location-master/cities', newCity);
      toast.success('City added successfully');
      setShowAddDialog(false);
      setNewCity({ state: '', district: '', city: '' });
      setMultipleCities([]);
      setCityInput('');
      setAddDialogDistricts([]);
      setDuplicateError('');
      fetchCities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add city');
    }
  };

  const handleEditCity = async () => {
    if (!editForm.state || !editForm.district || !editForm.city) {
      toast.error('All fields are required');
      return;
    }
    
    try {
      await api.put(`/location-master/cities/${editForm.id}`, {
        state: editForm.state,
        district: editForm.district,
        city: editForm.city
      });
      toast.success('City updated successfully');
      setShowEditDialog(false);
      setEditingCity(null);
      fetchCities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update city');
    }
  };

  const handleDeleteCity = async (city) => {
    if (!confirm(`Are you sure you want to delete ${city.city}, ${city.district}?`)) {
      return;
    }
    
    try {
      await api.delete(`/location-master/cities/${city.id}`);
      toast.success('City deleted successfully');
      fetchCities();
    } catch (error) {
      toast.error('Failed to delete city');
    }
  };

  const openEditDialog = (city) => {
    setEditingCity(city);
    setEditForm({
      id: city.id,
      state: city.state,
      district: city.district,
      city: city.city
    });
    setShowEditDialog(true);
  };

  const handleSeedCities = async () => {
    if (!confirm('This will add initial city data for major industrial cities. Continue?')) {
      return;
    }
    
    setSeeding(true);
    try {
      const response = await api.post('/location-master/seed-cities');
      toast.success(`Seeding complete! Added: ${response.data.added}, Skipped: ${response.data.skipped}`);
      setIsSeeded(true);
      fetchCities();
    } catch (error) {
      toast.error('Failed to seed cities');
    } finally {
      setSeeding(false);
    }
  };

  const clearFilters = () => {
    setSelectedState('');
    setSelectedDistrict('');
    setSearchQuery('');
    fetchCities();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
            City Management
          </h1>
          <p className="text-slate-600">Manage cities for job location selection</p>
        </div>
        <div className="flex items-center gap-2">
          {isSeeded ? (
            <span className="inline-flex items-center gap-1 px-3 py-2 text-sm text-green-700 bg-green-50 rounded-md border border-green-200">
              <CheckCircle className="w-4 h-4" />
              Initial data loaded
            </span>
          ) : (
            <Button 
              variant="outline" 
              onClick={handleSeedCities}
              disabled={seeding}
            >
              {seeding ? 'Seeding...' : 'Seed Initial Data'}
            </Button>
          )}
          <Button onClick={() => setShowAddDialog(true)} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-2" />
            Add City
          </Button>
        </div>
      </div>

      {/* Stats Card */}
      <Card className="p-6 border-slate-200 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Building className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{cities.length}</p>
            <p className="text-sm text-slate-600">
              {selectedState || selectedDistrict ? 'Filtered Cities' : 'Total Cities'}
            </p>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">State</label>
            <Select value={selectedState || 'all'} onValueChange={(v) => setSelectedState(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {states.map((state) => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">District</label>
            <Select 
              value={selectedDistrict || 'all'} 
              onValueChange={(v) => setSelectedDistrict(v === 'all' ? '' : v)}
              disabled={!selectedState}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Districts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Districts</SelectItem>
                {districts.map((d) => (
                  <SelectItem key={d.district} value={d.district}>{d.district}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Search City</label>
            <div className="flex gap-2">
              <Input
                placeholder="Search by city name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button variant="outline" onClick={handleSearch}>
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={clearFilters} className="w-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </div>
      </Card>

      {/* Cities Table */}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12 text-center">#</TableHead>
                <TableHead>City</TableHead>
                <TableHead>District</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : cities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">No cities found</p>
                    {!isSeeded && (
                      <p className="text-sm text-slate-400 mt-1">Click Seed Initial Data to add major industrial cities</p>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                cities.map((city, index) => (
                  <TableRow key={city.id || index} className="hover:bg-slate-50">
                    <TableCell className="text-center text-slate-500">{index + 1}</TableCell>
                    <TableCell className="font-medium">{city.city}</TableCell>
                    <TableCell>{city.district}</TableCell>
                    <TableCell>{city.state}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(city)}
                          className="text-slate-600 hover:text-teal-600"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCity(city)}
                          className="text-slate-600 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Add City Dialog - Multiple Cities Support */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          setNewCity({ state: '', district: '', city: '' });
          setMultipleCities([]);
          setCityInput('');
          setAddDialogDistricts([]);
          setDuplicateError('');
          setExistingCitiesInDistrict([]);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Cities</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">State <span className="text-red-500">*</span></label>
              <Select 
                value={newCity.state || 'placeholder'} 
                onValueChange={(val) => {
                  setNewCity({...newCity, state: val === 'placeholder' ? '' : val, district: ''});
                  setMultipleCities([]);
                  setCityInput('');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder" disabled>Select state</SelectItem>
                  {states.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">District <span className="text-red-500">*</span></label>
              <Select 
                value={newCity.district || 'placeholder'} 
                onValueChange={(val) => {
                  setNewCity({...newCity, district: val === 'placeholder' ? '' : val});
                  setMultipleCities([]);
                  setCityInput('');
                }}
                disabled={!newCity.state || addDialogDistricts.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={newCity.state ? "Select district" : "Select state first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder" disabled>Select district</SelectItem>
                  {addDialogDistricts.map((d) => (
                    <SelectItem key={d.district} value={d.district}>{d.district}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newCity.state && addDialogDistricts.length === 0 && (
                <p className="text-xs text-slate-500 mt-1">Loading districts...</p>
              )}
            </div>
            
            {/* City Input with Add Button */}
            <div>
              <label className="block text-sm font-medium mb-2">Add Cities</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter city name"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCityToList())}
                  className={duplicateError ? 'border-red-500' : ''}
                  disabled={!newCity.state || !newCity.district}
                />
                <Button 
                  type="button"
                  onClick={handleAddCityToList}
                  disabled={!cityInput.trim() || !!duplicateError || !newCity.district}
                  className="bg-teal-600 hover:bg-teal-700 shrink-0"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {duplicateError && (
                <p className="text-xs text-red-500 mt-1">{duplicateError}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">Press Enter or click + to add city to list</p>
            </div>

            {/* Cities List */}
            {multipleCities.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Cities to add ({multipleCities.length})
                </label>
                <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg max-h-32 overflow-y-auto">
                  {multipleCities.map((city, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
                    >
                      {city}
                      <button
                        onClick={() => handleRemoveCityFromList(index)}
                        className="hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveAllCities} 
              className="bg-teal-600 hover:bg-teal-700"
              disabled={multipleCities.length === 0 || !newCity.state || !newCity.district}
            >
              Save {multipleCities.length > 0 ? `${multipleCities.length} Cities` : 'Cities'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit City Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) {
          setEditForm({ id: '', state: '', district: '', city: '' });
          setEditDialogDistricts([]);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit City</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">State</label>
              <Select 
                value={editForm.state || 'placeholder'} 
                onValueChange={(val) => setEditForm({...editForm, state: val === 'placeholder' ? '' : val, district: ''})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder" disabled>Select state</SelectItem>
                  {states.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">District</label>
              <Select 
                value={editForm.district || 'placeholder'} 
                onValueChange={(val) => setEditForm({...editForm, district: val === 'placeholder' ? '' : val})}
                disabled={!editForm.state || editDialogDistricts.length === 0}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder" disabled>Select district</SelectItem>
                  {editDialogDistricts.map((d) => (
                    <SelectItem key={d.district} value={d.district}>{d.district}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">City Name</label>
              <Input
                value={editForm.city}
                onChange={(e) => setEditForm({...editForm, city: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditCity} className="bg-teal-600 hover:bg-teal-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CityManagement;
